const THEME_STORAGE_KEY = "illio-theme-preference";

export class ThemeService {
  private static instance: ThemeService;

  static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  /**
   * Get theme from localStorage
   */
  getStoredTheme(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(THEME_STORAGE_KEY);
  }

  /**
   * Save theme to localStorage
   */
  setStoredTheme(theme: string): void {
    if (typeof window === "undefined") return;
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }

  /**
   * Remove theme from localStorage
   */
  clearStoredTheme(): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(THEME_STORAGE_KEY);
  }

  /**
   * Get system preference for color scheme
   */
  getSystemPreference(): string {
    if (typeof window === "undefined" || !window.matchMedia) {
      return "light";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  /**
   * Get the effective theme (stored > system > default)
   */
  getEffectiveTheme(defaultTheme: string = "light"): string {
    return this.getStoredTheme() || this.getSystemPreference() || defaultTheme;
  }

  /**
   * Get the effective theme for a specific domain
   */
  getEffectiveThemeForDomain(isDomo: boolean): string {
    const defaultTheme = isDomo ? "dark" : "light";
    return this.getStoredTheme() || this.getSystemPreference() || defaultTheme;
  }

  /**
   * Sync localStorage theme with user preferences
   * This should be called when user preferences become available
   */
  async syncWithUserPreferences(
    updateMiscPreference: (key: string, value: any) => Promise<any>
  ): Promise<void> {
    const storedTheme = this.getStoredTheme();
    if (storedTheme) {
      try {
        await updateMiscPreference("theme", storedTheme);
        console.log(
          "Synced localStorage theme with user preferences:",
          storedTheme
        );
      } catch (error) {
        console.error("Failed to sync theme with user preferences:", error);
      }
    }
  }

  /**
   * Load theme from user preferences and update localStorage
   * This should be called when user preferences are loaded
   */
  loadFromUserPreferences(preferences: any): string | null {
    if (preferences?.misc?.theme) {
      const savedTheme = preferences.misc.theme.replaceAll('"', "");
      this.setStoredTheme(savedTheme);
      return savedTheme;
    }
    return null;
  }
}
